import {Component, OnInit, Input, Output, ViewChild, OnDestroy} from '@angular/core';
import { MdDialogRef } from '@angular/material';
import { ChipsSelectorComponent } from '../../chips-selector/chips-selector.component';
import { ColaboradorAutocompleteComponent } from '../../autocomplete/colaborador-autocomplete/colaborador-autocomplete.component';
import { AcopioAutocompleteComponent } from '../../autocomplete/acopio-autocomplete/acopio-autocomplete.component';
import { CuentaAutocompleteComponent } from '../../autocomplete/cuenta-autocomplete/cuenta-autocomplete.component';
import { CosechaService, ProductoService } from '../../../../services';
import { NegociosService } from '../../../../services/negocios-service';
import * as moment from 'moment';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs/Subscription';
import {fromEvent} from 'rxjs/observable/fromEvent';
import {RolesAutocompleteComponent} from '../../autocomplete';

export const FilterType = {
  number: 'number',
  numberRange: 'numberRange',
  string: 'string',
  date: 'date',
  dateRange: 'dateRange',
  chip: 'chip',
  colaborador: 'colab',
  acopio: 'aco',
  cosecha: 'cos',
  producto: 'prod',
  cliente: 'cli',
  tipoNegocio: 'tipoNeg',
  tipoPosicion: 'tipoPos',
  cuitRazon: 'cuitRazon',
  rol: 'rol',
  singleSelect: 'select'
};

export interface FilterSettings {
  FilterType: string;
  FilterTitle: string;
  FilterName: string; // nombre del filtro en el objeto que enviamos al back
  FilterPlaceholder?: string;
  PreviousNumberMin?: number;
  PreviousNumberMax?: number;
  PreviousString?: string;
  PreviousArrayNum?: number[];
  PreviousChipList?: ChipValues[]; // tambien usado para llenar los items del ChipList
  MinDate?: number;
  MaxDate?: number;
}

export interface ChipValues {
  ID: number;
  ItemLabel: string;
}

export interface DateRangeFilter {
  Desde: number;
  Hasta: number;
}

export interface NumberRangeFilter {
  minValue: number;
  maxValue: number;
}

@Component({
  selector: 'app-generic-filter-dialog',
  templateUrl: './generic-filter-dialog.component.html',
  styleUrls: ['./generic-filter-dialog.component.scss']
})
export class GenericFilterDialogComponent implements OnInit {

  @Input() filterSettings: FilterSettings;

  @ViewChild(ChipsSelectorComponent) chipsSelector;
  @ViewChild(ColaboradorAutocompleteComponent) colaboradorAutocomplete;
  @ViewChild(AcopioAutocompleteComponent) acopioAutocomplete;
  @ViewChild(CuentaAutocompleteComponent) cuentaAutocomplete;
  @ViewChild(RolesAutocompleteComponent) rolesAutocomplete: RolesAutocompleteComponent;

  FilterTypes = FilterType;

  stringValue = '';

  formGroup: FormGroup = null;

  selectedRoles = [];

  /** DatePicker **/
  @ViewChild('desdeDatePicker') desdeDatePicker;
  @ViewChild('hastaDatePicker') hastaDatePicker;
  minDate = moment.unix(0).startOf('day').toDate();
  maxDate = moment().endOf('day').toDate();
  fechaDesde: any = null;
  fechaHasta: any = null;

  /** Number Range **/
  numDesde = 0;
  numHasta = 0;

  singleOptions = [];
  singleValue = null;
  sub: Subscription;
  constructor(
    public dialogRef: MdDialogRef<GenericFilterDialogComponent>,
    private cosechaService: CosechaService,
    private productosService: ProductoService,
    private negociosService: NegociosService,
    private fb: FormBuilder,
  ) {
    this.sub = fromEvent(document, 'keydown').subscribe(e => {
      if (e['keyCode'] === 13) { this.onSubmit() }
    });
  }

  ngOnInit() {
    if (this.filterSettings) {
      switch (this.filterSettings.FilterType) {
        case FilterType.dateRange:
        case FilterType.date:
        case FilterType.numberRange:
        case FilterType.string:
        case FilterType.number:
        case FilterType.cosecha:
        case FilterType.cuitRazon:
        case FilterType.rol:
        case FilterType.singleSelect:
          this.setupFilter();
      }
    }
  }

  setupFilter() {
    switch (this.filterSettings.FilterType) {
      case FilterType.chip:
        if (this.filterSettings.PreviousChipList) {
          this.chipsSelector.optionsChange(this.filterSettings.PreviousChipList, this.filterSettings.PreviousArrayNum);
          this.chipsSelector.placeholder = this.filterSettings.FilterPlaceholder;
        }
        break;

      case FilterType.colaborador:
        if (this.filterSettings.PreviousChipList) {
          this.colaboradorAutocomplete.selectedColaboradores = this.filterSettings.PreviousChipList.map(x => ({UserId: x.ID, UserFullName: x.ItemLabel, canViewProduct: true}));
        }
        break;

      case FilterType.acopio:
        if (this.filterSettings.PreviousChipList) {
          this.acopioAutocomplete.selectedAcopios = this.filterSettings.PreviousChipList.map(x => ({Id: x.ID, AcopioNombre: x.ItemLabel}));
        }
        break;

      case FilterType.cliente:
        if (this.filterSettings.PreviousChipList) {
          this.cuentaAutocomplete.selectedCuentas = this.filterSettings.PreviousChipList.map(x => ({Id: x.ID, FullName: x.ItemLabel}));
        }
        break;

      case FilterType.cosecha:
        this.cosechaService.loadOptions();
        this.cosechaService.Options.subscribe(x => {
          this.singleOptions = x.map(y => ({Id: y.ID, ItemLabel: y.Cosecha}));
          this.singleValue = this.filterSettings.PreviousNumberMin ? this.filterSettings.PreviousNumberMin : null;
        });
        break;

      case FilterType.producto:
        this.productosService.loadOptions();
        this.productosService.Options.subscribe(x => {
          this.chipsSelector.optionsChange(x.map(y => ({ItemLabel: y.Producto, ID: y.ID})), this.filterSettings.PreviousArrayNum ? this.filterSettings.PreviousArrayNum : []);
        });
        break;

      case FilterType.string:
        this.stringValue = this.filterSettings.PreviousString ? this.filterSettings.PreviousString : '';
        break;

      case FilterType.tipoNegocio:                                              // nos aseguramos de usar una copia
        this.chipsSelector.optionsChange(this.negociosService.getTipoSelectOptions().slice().map(y => ({ItemLabel: y.Tipo_Negocio, ID: y.ID})), this.filterSettings.PreviousArrayNum ? this.filterSettings.PreviousArrayNum : []);
        break;

      case FilterType.numberRange:
        this.numDesde = this.filterSettings.PreviousNumberMin ? this.filterSettings.PreviousNumberMin : 0;
        this.numHasta = this.filterSettings.PreviousNumberMax ? this.filterSettings.PreviousNumberMax : 0;
        break;

      case FilterType.dateRange:
        if (this.filterSettings.PreviousNumberMin) { this.fechaDesde = moment.unix(this.filterSettings.PreviousNumberMin).toDate(); }
        if (this.filterSettings.PreviousNumberMax) { this.fechaHasta = moment.unix(this.filterSettings.PreviousNumberMax).toDate(); }
        if (this.filterSettings.MinDate && this.filterSettings.MaxDate) {
          this.minDate = moment.unix(this.filterSettings.MinDate).toDate();
          this.maxDate = moment.unix(this.filterSettings.MaxDate).toDate();
        }
        break;

      case FilterType.number:
        this.numDesde = this.filterSettings.PreviousNumberMin ? this.filterSettings.PreviousNumberMin : 0;
        break;

      case FilterType.date:
        if (this.filterSettings.PreviousNumberMin) { this.fechaDesde = moment.unix(this.filterSettings.PreviousNumberMin).toDate(); }
        if (this.filterSettings.MinDate && this.filterSettings.MaxDate) {
          this.minDate = moment.unix(this.filterSettings.MinDate).toDate();
          this.maxDate = moment.unix(this.filterSettings.MaxDate).toDate();
        }
        break;

      case FilterType.tipoPosicion:
        this.singleValue = this.filterSettings.PreviousString ? this.filterSettings.PreviousString : null;
        break;

      case FilterType.cuitRazon:
        this.formGroup = this.fb.group({
          razon: this.filterSettings.PreviousString ? this.filterSettings.PreviousString : '',
          cuit: this.filterSettings.PreviousNumberMin ? this.filterSettings.PreviousNumberMin : null,
        });
        break;

      case FilterType.rol:
        if (this.filterSettings.PreviousArrayNum) {
          this.selectedRoles = this.filterSettings.PreviousArrayNum;
        }
        break;

      case FilterType.singleSelect:
        if (this.filterSettings.PreviousString) {
          this.singleValue = this.filterSettings.PreviousString;
        }
        break;
    }
  }

  openDatePicker(e) {
    if (e === 'desde') {
      this.desdeDatePicker.open();
    } else {
      this.hastaDatePicker.open();
    }
  }

  selectedDate(e, item) {
    const startOfDay = moment(e).startOf('day').toDate();
    const endOfDay = moment().endOf('day').toDate();

    if (item === 'desde') {
      if (this.fechaHasta === null) {
        this.fechaHasta = endOfDay;
      }
      this.fechaDesde = moment(e).startOf('day').toDate();
      // si se elige una fecha de inicio Mayor a fecha de fin, mover la fecha de fin
      if (this.fechaHasta < e) {
        this.fechaHasta = endOfDay;
      }
    } else {
      if (this.fechaDesde === null) {
        this.fechaDesde = startOfDay;
      }
      this.fechaHasta = moment(e).endOf('day').toDate();
      // si se elige una fecha de fin Menor a fecha de inicio, mover la fecha de inicio
      if (this.fechaDesde > e) {
        this.fechaDesde = startOfDay;
      }
    }
  }

  eliminarFechas() {
    this.fechaDesde = null;
    this.fechaHasta = null;
  }

  onSubmit() {
    // generamos el objeto a devolver
    const filterObj = {
      FilterType: this.filterSettings.FilterType,
      FilterTitle: this.filterSettings.FilterTitle,
      FilterName: this.filterSettings.FilterName,
      // Siempre usaremos el MinValue en caso de ser un filtro de un solo valor, ya sea un string o un array
      MinValue: null,
      MaxValue: null
    };

    switch (this.filterSettings.FilterType) {
      case FilterType.colaborador:
        filterObj.MinValue = this.colaboradorAutocomplete.selectedColaboradores.length ? this.colaboradorAutocomplete.selectedColaboradores.map(x => ({ID: x.UserId, ItemLabel: x.UserFullName})) : null;
        break;

      case FilterType.acopio:
        filterObj.MinValue = this.acopioAutocomplete.selectedAcopios.length ? this.acopioAutocomplete.selectedAcopios.map(x => ({ItemLabel: x.AcopioNombre, ID: x.Id})) : null;
        break;

      case FilterType.cliente:
        filterObj.MinValue = this.cuentaAutocomplete.selectedCuentas.length ? this.cuentaAutocomplete.selectedCuentas.map(x => ({ID: x.Id, ItemLabel: x.FullName})) : null;
        break;

      case FilterType.producto:
      case FilterType.tipoNegocio:
        filterObj.MinValue = this.chipsSelector.selectedIds.length ? this.chipsSelector.selectedIds : null;
        break;

      case FilterType.string:
        filterObj.MinValue = this.stringValue.trim().length ? this.stringValue.trim() : null;
        break;

      case FilterType.numberRange:
        if (!(this.numDesde === 0 && this.numHasta === 0)) {
          // si desde es mayor que hasta, los intercambiamos
          if (this.numDesde > this.numHasta) {
            filterObj.MinValue = this.numHasta;
            filterObj.MaxValue = this.numDesde;
          } else {
            filterObj.MinValue = this.numDesde;
            filterObj.MaxValue = this.numHasta;
          }
        }
        break;

      case FilterType.dateRange:
        if (this.fechaDesde !== null) { filterObj.MinValue = moment(this.fechaDesde).unix(); }
        if (this.fechaHasta !== null) { filterObj.MaxValue = moment(this.fechaHasta).unix(); }
        break;

      case FilterType.number:
        if (this.numDesde !== 0) {
          filterObj.MinValue = this.numDesde;
        }
        break;

      case FilterType.date:
        if (this.fechaDesde !== null) { filterObj.MinValue = moment(this.fechaDesde).unix(); }
        break;

      case FilterType.cosecha:
        filterObj.MinValue = this.singleValue;
        break;

      case FilterType.tipoPosicion:
        filterObj.MinValue = this.singleValue;
        break;

      case FilterType.cuitRazon:
        const cuit = this.formGroup.get('cuit').value;
        filterObj.MinValue = this.formGroup.get('razon').value.trim().length ? this.formGroup.get('razon').value.trim() : null;
        filterObj.MaxValue = cuit && cuit.trim().length ? this.formGroup.get('cuit').value.trim() : null;
        break;

      case FilterType.rol:
        filterObj.MaxValue = this.rolesAutocomplete.selectedRoles.map(x => x.RolName);
        filterObj.MinValue = this.rolesAutocomplete.selectedRoles.map(x => x.Id);
        break;

      case FilterType.singleSelect:
        filterObj.MinValue = this.singleValue;
        filterObj.MaxValue = this.singleValue !== null ? this.filterSettings.PreviousChipList[
            this.filterSettings.PreviousChipList.findIndex(x => x.ID === this.singleValue)
          ].ItemLabel : null;
        break;

      case FilterType.chip:
        filterObj.MinValue = this.chipsSelector.selectedIds.length ? this.chipsSelector.selectedIds : null;
        filterObj.MaxValue = this.chipsSelector.selectedIds.length ?
            // devolvemos los nombres en caso de que se puedan requerir
            this.chipsSelector.selectedIds.map(x => this.filterSettings.PreviousChipList.find(y => y.ID === x).ItemLabel)
            : null;
        break;
    }
    this.sub.unsubscribe();
    this.dialogRef.close(filterObj);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/common/dialog/generic-filter-dialog/generic-filter-dialog.component.ts